<script setup lang="ts">
import type { Form, FormSubmitEvent } from '#ui/types';
import { phoneValidator } from '@lib/shared';
import lodashSnakeCase from 'lodash-es/snakeCase';
import { z } from 'zod';

import {
    type BackendErrorEntity,
    type InsertContactSaleFormRequestBodyDto,
    type ErrorType,
    type Language,
    useApiInsertContactSaleFormRequest,
} from '~/apiClient';
import { AUTOCOMPLETE_OFF } from '~/constants/form';
import { ROUTE_NAMES } from '~/constants/routes';
import { isAuction, isDirectSale, type SaleItemEntity } from '~/types/sale.type';

// PROPS
const { sale, salesContact } = defineProps({
    sale: { type: Object as PropType<SaleItemEntity>, required: true },
    salesContact: { type: Object as PropType<SalesContact>, required: true },
});

// EVENTS
const emit = defineEmits<{
    success: [];
}>();

// MODEL
const isModalOpen = defineModel<boolean>({ required: true });

// COMPOSABLES
const { isMobile } = useResponsive();
const { tt } = useTypedI18n();
const { mutateAsync: mutateSubmit, isPending } = useApiInsertContactSaleFormRequest();
const { notifySuccess, notifyBackendError, notifyGenericError } = useNotification();
const { data: authData } = useAuthUtils();
const { isBundle, isBundleMulti } = useSaleType(() => sale.type);
const { title: saleTitle } = useSaleTitle(() => sale);
const { logError } = useLogs();
const { locale } = useI18n();
const { getFormErrors } = useBackendErrors();
const {
    trackSalesContactFocusContactFormField,
    trackSalesContactContactFormValidationError,
    trackSalesContactSubmitContactForm,
    trackSalesContactContactFormSent,
} = useTracking();
const { getPlatformBusClassesLabel } = usePlatformBusClass();

// DATA
const formSchema = z.object({
    name: zodRequiredString(tt('validation.msg.required', { field: tt('salesContact.contactForm.name') })),
    email: zodRequiredString(tt('validation.msg.required', { field: tt('salesContact.contactForm.email') })).email(
        tt('validation.msg.invalid', { field: tt('salesContact.contactForm.email') })
    ),
    phone: z.string().superRefine((value, ctx) => {
        if (value && !phoneValidator(value)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: tt('validation.msg.phone'),
                params: { customErrorCause: 'invalid' },
            });
        }
    }),
    companyBusinessName: z.string(),
    message: zodRequiredString(tt('validation.msg.required', { field: tt('salesContact.contactForm.message') })),
});

type FormSchema = z.infer<typeof formSchema>;

const getInitialFormData = (): FormSchema => ({
    name: authData.value?.user ? `${authData.value.user.firstname} ${authData.value.user.lastname}` : '',
    email: authData.value?.user?.email ?? '',
    phone: authData.value?.user?.phone ?? '',
    companyBusinessName: authData.value?.user?.company.businessName ?? '',
    message: tt('salesContact.contactForm.messagePrefill', { title: saleTitle.value }),
});

const formData = reactive<FormSchema>(getInitialFormData());

const form = ref<Form<FormData>>();

// METHODS

const onSubmitClick = (): void => {
    trackSalesContactSubmitContactForm();
    form.value?.submit();
};

const onSubmit = (event: FormSubmitEvent<FormSchema>): void => {
    const { data } = event;

    mutateSubmit({
        data: {
            auctionId: isAuction(sale) ? sale.id : undefined,
            directSaleId: isDirectSale(sale) ? sale.id : undefined,
            language: locale.value as Language,
            salesAgentEmail: salesContact.email,
            name: data.name,
            email: data.email,
            phone: data.phone || undefined,
            companyBusinessName: data.companyBusinessName || undefined,
            message: data.message,
        },
    })
        .then(() => {
            notifySuccess({ title: tt('salesContact.contactForm.submitSuccess') });
            trackSalesContactContactFormSent(data.email, data.phone);
            emit('success');
        })
        .catch((apiError: ErrorType<BackendErrorEntity>) => {
            logError('Error in submit sales contact form', { formData: data, error: apiError });

            if (!apiError.data?.error) {
                notifyGenericError();

                return;
            }

            const { error } = apiError.data;
            notifyBackendError({ error });
            const formErrors = getFormErrors<InsertContactSaleFormRequestBodyDto, FormSchema>(apiError, {
                name: 'name',
                email: 'email',
                phone: 'phone',
                companyBusinessName: 'companyBusinessName',
                message: 'message',
            });
            form.value?.setErrors(formErrors);
        });
};

const onFieldFocus = (fieldName: string): void => {
    trackSalesContactFocusContactFormField(fieldName);
};

const onFormError = (): void => {
    const validation = formSchema.safeParse(formData);

    const errorTypes = (validation.error?.issues ?? []).map(issue => {
        const errorCause = ((): string => {
            if (issue.code === 'too_small') return 'required';
            if (issue.code === 'invalid_format') return 'invalid';
            if (issue.code === 'custom' && issue?.params?.customErrorCause) return String(issue.params.customErrorCause);

            return 'unknown';
        })();

        return lodashSnakeCase([...issue.path, errorCause].join('_'));
    });

    errorTypes.forEach(errorType => {
        trackSalesContactContactFormValidationError(errorType);
    });
};

// WATCHERS
watch(isModalOpen, (value, oldValue) => value && !oldValue && Object.assign(formData, getInitialFormData()));
</script>

<template>
    <UModal v-model:open="isModalOpen" :fullscreen="isMobile" :title="tt('salesContact.contactForm.title')" v-bind="modalNoDescAriaFixupProps">
        <template #body>
            <UForm ref="form" class="w-full space-y-4" :state="formData" :schema="formSchema" @submit="onSubmit" @error="onFormError">
                <div>
                    <p class="text-lg font-medium">{{ saleTitle }}</p>

                    <div class="text-toned">
                        <p v-if="!isBundleMulti">{{ getPlatformBusClassesLabel(sale.mainAsset.class) }}</p>
                        <p v-if="!isBundle">VIN: {{ sale.mainAsset.vin }}</p>
                    </div>
                </div>

                <UFormField :label="tt('salesContact.contactForm.name')" name="name" required>
                    <UInput v-model.trim="formData.name" :disabled="isPending" :autocomplete="AUTOCOMPLETE_OFF" @focus="onFieldFocus('name')" />
                </UFormField>

                <UFormField :label="tt('salesContact.contactForm.email')" name="email" required>
                    <UInput v-model.trim="formData.email" :disabled="isPending" :autocomplete="AUTOCOMPLETE_OFF" @focus="onFieldFocus('email')" />
                </UFormField>

                <UFormField :label="tt('salesContact.contactForm.phone')" name="phone">
                    <UInput v-model.trim="formData.phone" :disabled="isPending" :autocomplete="AUTOCOMPLETE_OFF" @focus="onFieldFocus('phone')" />
                </UFormField>

                <UFormField :label="tt('salesContact.contactForm.company')" name="companyBusinessName">
                    <UInput
                        v-model.trim="formData.companyBusinessName"
                        :disabled="isPending"
                        :autocomplete="AUTOCOMPLETE_OFF"
                        @focus="onFieldFocus('companyBusinessName')"
                    />
                </UFormField>

                <UFormField :label="tt('salesContact.contactForm.message')" name="message" required>
                    <UTextarea v-model="formData.message" :autocomplete="AUTOCOMPLETE_OFF" :rows="4" autoresize @focus="onFieldFocus('message')" />
                </UFormField>

                <i18n-t scope="global" tag="p" class="text-dimmed text-sm" keypath="salesContact.contactForm.consentNotice">
                    <template #terms>
                        <NuxtLinkLocale class="underline" :to="ROUTE_NAMES.TERMS_AND_CONDITIONS" target="_blank">
                            {{ tt('termsConditions.link') }}
                        </NuxtLinkLocale>
                    </template>
                    <template #privacy>
                        <NuxtLinkLocale class="underline" :to="ROUTE_NAMES.PRIVACY_POLICY" target="_blank">
                            {{ tt('privacy.link') }}
                        </NuxtLinkLocale>
                    </template>
                </i18n-t>
            </UForm>
        </template>
        <template #footer>
            <UButton size="xl" :label="tt('salesContact.contactForm.submitButton')" :loading="isPending" block @click="onSubmitClick" />
        </template>
    </UModal>
</template>
